// Buttons. Primary is the iOS PrimaryButtonStyle (orange gradient fill, white label, soft orange shadow), secondary
// the tonal SecondaryButtonStyle (surface + hairline), ghost a text button for toolbars, destructive the red fill
// used for "Delete permanently". The press-scale of PressableStyle becomes a small active scale here.

import type { ButtonHTMLAttributes, ReactNode, Ref } from 'react';
import { cn } from './cn';
import { Spinner } from './Feedback';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'destructive';
export type ButtonSize = 'sm' | 'md' | 'lg';

const BASE =
  'relative inline-flex shrink-0 select-none items-center justify-center gap-2 whitespace-nowrap font-semibold ' +
  'transition-[background-color,box-shadow,color,transform,opacity] duration-150 ease-out ' +
  'active:scale-[0.97] disabled:pointer-events-none disabled:opacity-50 aria-busy:cursor-progress ' +
  '[&_svg]:shrink-0';

const VARIANT: Record<ButtonVariant, string> = {
  primary:
    'gt-primary-fill text-primary-contrast shadow-primary hover:bg-primary-hover active:bg-primary-active ' +
    'disabled:shadow-none',
  secondary:
    'bg-surface text-fg border border-line-strong shadow-[0_1px_2px_rgb(16_42_94/0.05)] hover:bg-surface-2 ' +
    'active:bg-surface-3',
  ghost: 'bg-transparent text-fg hover:bg-surface-3/70 active:bg-surface-3',
  destructive: 'gt-danger-fill text-white shadow-[0_6px_14px_-6px_rgb(219_64_56/0.5)] hover:bg-danger-hover',
};

const SIZE: Record<ButtonSize, string> = {
  sm: 'h-8 rounded-[10px] px-3 text-[13px] [&_svg]:size-3.5',
  md: 'h-9 rounded-control px-4 text-sm [&_svg]:size-4',
  lg: 'h-11 rounded-tile px-5 text-[15px] [&_svg]:size-[18px]',
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Shows a spinner in place of the icon, disables the button and sets aria-busy; the label stays. */
  loading?: boolean;
  /** Leading icon (a lucide element); sized by the button. */
  icon?: ReactNode;
  /** Trailing icon (chevrons, external-link marks). */
  iconRight?: ReactNode;
  fullWidth?: boolean;
  ref?: Ref<HTMLButtonElement>;
}

export function Button({
  variant = 'secondary',
  size = 'md',
  loading = false,
  icon,
  iconRight,
  fullWidth,
  type = 'button',
  disabled,
  className,
  children,
  ref,
  ...rest
}: ButtonProps) {
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...rest}
      className={cn(BASE, VARIANT[variant], SIZE[size], fullWidth && 'w-full', className)}
    >
      {loading ? <Spinner size={size === 'lg' ? 'md' : 'sm'} label={null} /> : icon}
      {children}
      {iconRight}
    </button>
  );
}

export type IconButtonVariant = ButtonVariant;

const ICON_SIZE: Record<ButtonSize, string> = {
  sm: 'size-8 rounded-[10px] [&_svg]:size-4',
  md: 'size-9 rounded-control [&_svg]:size-[18px]',
  lg: 'size-11 rounded-tile [&_svg]:size-5',
};

export interface IconButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  /** The accessible name (and the hover title unless `title` is given). Required: an icon alone says nothing. */
  label: string;
  icon: ReactNode;
  variant?: IconButtonVariant;
  size?: ButtonSize;
  /** Circle instead of the rounded square (map controls, GlassIconButton). */
  round?: boolean;
  loading?: boolean;
  /** For toggle buttons (filters, layer switches). */
  pressed?: boolean;
  ref?: Ref<HTMLButtonElement>;
}

/** A square icon-only button with a required accessible label. */
export function IconButton({
  label,
  icon,
  variant = 'ghost',
  size = 'md',
  round,
  loading,
  pressed,
  type = 'button',
  disabled,
  title,
  className,
  ref,
  ...rest
}: IconButtonProps) {
  return (
    <button
      ref={ref}
      type={type}
      aria-label={label}
      title={title ?? label}
      aria-pressed={pressed}
      aria-busy={loading || undefined}
      disabled={disabled || loading}
      {...rest}
      className={cn(
        BASE,
        VARIANT[variant],
        ICON_SIZE[size],
        variant === 'ghost' && 'text-fg-muted hover:text-fg',
        pressed && variant === 'ghost' && 'bg-primary-soft text-primary-text hover:bg-primary-soft',
        round && 'rounded-full',
        className,
      )}
    >
      {loading ? <Spinner size="sm" label={null} /> : icon}
    </button>
  );
}
